import React, { useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, ExternalLink, Facebook } from "lucide-react";
import { useSite } from "../context/AppContext";

const BOOK_URL = "https://www.evotravelagent.com/ldixontravel";

export default function Header() {
  const { site } = useSite();
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const nav = site?.navigation?.items?.filter((i) => i.visible) || [];
  const cta = site?.navigation?.headerCta || { label: "Plan Your Trip", url: BOOK_URL, newTab: true };
  const logo = site?.branding?.logoUrl;

  return (
    <header className="sticky top-0 z-50 bg-[#061325]/90 backdrop-blur-xl border-b border-amber-500/20 shadow-[0_12px_40px_rgba(6,19,37,0.4)]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 flex items-center justify-between h-[72px]">
        <Link to="/" data-testid="brand-logo-link" className="flex items-center gap-3 shrink-0" aria-label="LD Travel home">
          {logo ? (
            <img src={logo} alt="LD Travel logo" className="h-12 w-12 rounded-full object-cover ring-1 ring-amber-400/40" />
          ) : (
            <span className="font-heading text-2xl text-amber-300 font-semibold">LD</span>
          )}
          <span className="hidden sm:block font-heading text-xl text-amber-100 tracking-wide">LD Travel</span>
        </Link>

        <nav aria-label="Main navigation" className="hidden lg:flex items-center gap-1">
          {nav.map((item) =>
            item.external ? (
              <a
                key={item.id}
                href={item.path}
                {...(item.newTab ? { target: "_blank", rel: "noopener noreferrer" } : {})}
                data-testid={`nav-link-${item.id}`}
                className="px-3.5 py-2 text-[13px] font-medium tracking-wide rounded-full transition-colors text-amber-300 hover:text-amber-100 hover:bg-white/5 inline-flex items-center gap-1"
              >
                {item.label}
                <Facebook className="w-3.5 h-3.5" aria-hidden="true" />
                {item.newTab && <span className="sr-only">(opens Facebook in a new tab)</span>}
              </a>
            ) : (
              <NavLink
                key={item.id}
                to={item.path}
                data-testid={`nav-link-${item.id}`}
                className={({ isActive }) =>
                  `px-3.5 py-2 text-[13px] font-medium tracking-wide rounded-full transition-colors ${
                    isActive ? "text-amber-300 bg-white/5" : "text-slate-200 hover:text-amber-200 hover:bg-white/5"
                  }`
                }
              >
                {item.label}
              </NavLink>
            )
          )}
        </nav>

        <div className="flex items-center gap-3">
          <a
            href={cta.url}
            {...(cta.newTab ? { target: "_blank", rel: "noopener noreferrer" } : {})}
            data-testid="nav-button-plan-trip"
            className="btn-brand hidden sm:inline-flex !py-2.5 !px-5 text-sm"
          >
            {cta.label}
            {cta.newTab && <ExternalLink className="w-3.5 h-3.5" aria-hidden="true" />}
            {cta.newTab && <span className="sr-only">(opens Lisa's travel website in a new tab)</span>}
          </a>
          <button
            data-testid="mobile-menu-button"
            className="lg:hidden text-amber-100 p-2 -mr-2"
            onClick={() => setOpen(!open)}
            aria-expanded={open}
            aria-label={open ? "Close menu" : "Open menu"}
          >
            {open ? <X className="w-7 h-7" /> : <Menu className="w-7 h-7" />}
          </button>
        </div>
      </div>

      {open && (
        <nav aria-label="Mobile navigation" className="lg:hidden border-t border-amber-500/15 bg-[#061325]/95 backdrop-blur-xl" data-testid="mobile-menu">
          <div className="px-5 py-4 flex flex-col gap-1">
            {nav.map((item) =>
              item.external ? (
                <a
                  key={item.id}
                  href={item.path}
                  {...(item.newTab ? { target: "_blank", rel: "noopener noreferrer" } : {})}
                  data-testid={`mobile-nav-link-${item.id}`}
                  onClick={() => setOpen(false)}
                  className="py-3.5 px-3 rounded-xl text-base font-medium text-amber-300 inline-flex items-center gap-2"
                >
                  {item.label} <Facebook className="w-4 h-4" aria-hidden="true" />
                  {item.newTab && <span className="sr-only">(opens Facebook in a new tab)</span>}
                </a>
              ) : (
                <Link
                  key={item.id}
                  to={item.path}
                  data-testid={`mobile-nav-link-${item.id}`}
                  onClick={() => setOpen(false)}
                  className={`py-3.5 px-3 rounded-xl text-base font-medium ${
                    location.pathname === item.path ? "text-amber-300 bg-white/5" : "text-slate-100"
                  }`}
                >
                  {item.label}
                </Link>
              )
            )}
            <a
              href={cta.url}
              {...(cta.newTab ? { target: "_blank", rel: "noopener noreferrer" } : {})}
              data-testid="mobile-nav-button-plan-trip"
              className="btn-brand mt-2 justify-center"
            >
              {cta.label}
              {cta.newTab && <ExternalLink className="w-4 h-4" aria-hidden="true" />}
            </a>
          </div>
        </nav>
      )}
    </header>
  );
}
